import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  alpha,
  Box,
  Button,
  Chip,
  CircularProgress,
  InputAdornment,
  MenuItem,
  Pagination,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  OpenInNew as DetailsIcon,
  Search as SearchIcon,
  Apps as ModuleIcon,
  CheckCircle as WorkingIcon,
  WarningAmber as PartialIcon,
  HighlightOff as NotWorkingIcon,
  Payments as FeeIcon,
  FactCheck as AttendanceIcon,
  Badge as StaffAttIcon,
  MenuBook as HomeworkIcon,
  AccountBalanceWallet as ExpenseIcon,
  Campaign as NoticeIcon,
  Event as HolidayIcon,
  EventBusy as LeaveIcon,
  DirectionsBus as TransportIcon,
  Inventory2 as AccessoriesIcon,
  RequestQuote as SalaryIcon,
  Quiz as ExamIcon,
  Groups as RteIcon,
  Assignment as TcIcon,
  Chat as WhatsAppIcon,
  Celebration as EventIcon,
} from "@mui/icons-material";
import { useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import PageContentLayout from "../components/layout/PageContentLayout";
import apiService from "../services/apiService";
import { crmColors } from "../theme/colors";

//Created By Cursor
const PERIODS = [
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "this_week", label: "This Week" },
  { value: "last_week", label: "Last Week" },
  { value: "last_7_days", label: "Last 7 Days" },
  { value: "last_10_days", label: "Last 10 Days" },
  { value: "last_15_days", label: "Last 15 Days" },
  { value: "this_month", label: "This Month" },
  { value: "last_month", label: "Last Month" },
  { value: "last_30_days", label: "Last 30 Days" },
];

//Created By Cursor — selectable top modules (Icon = component; no login)
const MODULES = [
  { value: "fee", label: "Fee collection", short: "Fee", color: crmColors.energy.main, Icon: FeeIcon },
  {
    value: "attendance",
    label: "Student attendance",
    short: "Stu att.",
    color: crmColors.warning.main,
    Icon: AttendanceIcon,
  },
  {
    value: "staff_attendance",
    label: "Staff attendance",
    short: "Staff att.",
    color: crmColors.secondary.main,
    Icon: StaffAttIcon,
  },
  {
    value: "homework",
    label: "Homework",
    short: "HW",
    color: crmColors.primary.dark || crmColors.primary.main,
    Icon: HomeworkIcon,
  },
  { value: "expense", label: "Expense", short: "Expense", color: crmColors.error.main, Icon: ExpenseIcon },
  { value: "notice", label: "Notice", short: "Notice", color: crmColors.primary.main, Icon: NoticeIcon },
  { value: "holidays", label: "Holidays", short: "Holiday", color: "#6A1B9A", Icon: HolidayIcon },
  { value: "leave", label: "Leave", short: "Leave", color: "#00838F", Icon: LeaveIcon },
  { value: "transport", label: "Transport", short: "Transport", color: "#EF6C00", Icon: TransportIcon },
  {
    value: "accessories",
    label: "Accessories",
    short: "Accessories",
    color: "#5D4037",
    Icon: AccessoriesIcon,
  },
  {
    value: "staff_salary",
    label: "Staff salary",
    short: "Salary",
    color: "#2E7D32",
    Icon: SalaryIcon,
  },
  { value: "exam", label: "Exam", short: "Exam", color: "#1565C0", Icon: ExamIcon },
  { value: "rte", label: "RTE (Admission)", short: "RTE", color: "#AD1457", Icon: RteIcon },
  { value: "tc", label: "TC (Admission)", short: "TC", color: "#455A64", Icon: TcIcon },
  { value: "whatsapp", label: "WhatsApp", short: "WhatsApp", color: "#128C7E", Icon: WhatsAppIcon },
  { value: "event", label: "Event", short: "Event", color: "#C2185B", Icon: EventIcon },
];

const PAGE_SIZES = [50, 100, 200, 500];
const SORT_OPTIONS = [
  { value: "score_desc", label: "Most working ↓" },
  { value: "score_asc", label: "Least working ↑" },
  { value: "name_asc", label: "Name A–Z" },
  { value: "name_desc", label: "Name Z–A" },
  { value: "code_asc", label: "Code A–Z" },
  { value: "code_desc", label: "Code Z–A" },
];

const STATUS_OPTIONS = [
  { value: "all", label: "All schools" },
  { value: "working", label: "All modules working" },
  { value: "partial", label: "Partial" },
  { value: "not_working", label: "None working" },
];

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    bgcolor: "#fff",
    fontWeight: 600,
    fontSize: 13,
  },
};

//Created By Cursor
const formatDate = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
};

const moduleMeta = (value) => MODULES.find((m) => m.value === value) || MODULES[0];

const statusTone = (overall) => {
  if (overall === "Working") return crmColors.success.main;
  if (overall === "Partial") return crmColors.warning.main;
  return crmColors.error.main;
};

//Created By Cursor
const SummaryTile = ({ label, value, color, icon, active, onClick }) => (
  <Box
    onClick={onClick}
    sx={{
      flex: "1 1 140px",
      minWidth: 130,
      p: 1.5,
      borderRadius: "14px",
      cursor: onClick ? "pointer" : "default",
      border: `1.5px solid ${active ? color : alpha(color, 0.28)}`,
      background: active
        ? `linear-gradient(145deg, ${alpha(color, 0.18)} 0%, #fff 72%)`
        : `linear-gradient(145deg, ${alpha(color, 0.08)} 0%, #fff 70%)`,
      boxShadow: active ? `0 6px 14px -10px ${alpha(color, 0.55)}` : "none",
      display: "flex",
      alignItems: "center",
      gap: 1.2,
      transition: "border-color 0.15s ease, box-shadow 0.15s ease",
      "&:hover": onClick
        ? { borderColor: color, bgcolor: alpha(color, 0.06) }
        : undefined,
    }}
  >
    <Box
      sx={{
        width: 36,
        height: 36,
        borderRadius: "10px",
        display: "grid",
        placeItems: "center",
        bgcolor: alpha(color, 0.14),
        color,
        "& .MuiSvgIcon-root": { fontSize: 20 },
      }}
    >
      {icon}
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography
        sx={{
          fontSize: 10.5,
          fontWeight: 800,
          letterSpacing: "0.05em",
          textTransform: "uppercase",
          color: crmColors.text.secondary,
        }}
      >
        {label}
      </Typography>
      <Typography sx={{ fontWeight: 800, fontSize: "1.25rem", lineHeight: 1.15, color: crmColors.text.primary }}>
        {value}
      </Typography>
    </Box>
  </Box>
);

const SELECTED_GOLD = crmColors.logo.brand;
const SELECTED_GREEN = crmColors.success.main;

//Created By Cursor
const ModulePickCard = ({ meta, selected, stats, onToggle }) => {
  const pct = stats?.pct ?? 0;
  const working = stats?.working_count ?? 0;
  return (
    <Box
      onClick={onToggle}
      sx={{
        position: "relative",
        width: "100%",
        minWidth: 0,
        p: 1.35,
        pr: selected ? 2.1 : 1.35,
        borderRadius: "14px",
        cursor: "pointer",
        border: `1.5px solid ${
          selected ? SELECTED_GOLD : alpha(meta.color, 0.22)
        }`,
        background: selected
          ? `linear-gradient(135deg, ${alpha(SELECTED_GOLD, 0.5)} 0%, ${alpha(
              "#C8E6A0",
              0.8
            )} 48%, ${alpha(SELECTED_GREEN, 0.16)} 100%)`
          : `linear-gradient(155deg, ${alpha(meta.color, 0.07)} 0%, #fff 70%)`,
        boxShadow: selected
          ? `0 0 0 1px ${alpha(SELECTED_GOLD, 0.4)}, 0 8px 18px ${alpha(
              SELECTED_GOLD,
              0.24
            )}`
          : "none",
        transition: "transform 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease",
        "&:hover": {
          transform: "translateY(-1px)",
          borderColor: selected ? SELECTED_GOLD : meta.color,
          boxShadow: selected
            ? `0 0 0 1px ${alpha(SELECTED_GOLD, 0.45)}, 0 10px 20px ${alpha(
                SELECTED_GOLD,
                0.28
              )}`
            : `0 6px 14px ${alpha(meta.color, 0.12)}`,
        },
      }}
    >
      {selected ? (
        <WorkingIcon
          sx={{
            position: "absolute",
            top: 6,
            right: 6,
            fontSize: 18,
            color: SELECTED_GREEN,
            filter: `drop-shadow(0 0 1px ${alpha(SELECTED_GOLD, 0.55)})`,
          }}
        />
      ) : null}
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.85 }}>
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: "9px",
            display: "grid",
            placeItems: "center",
            bgcolor: selected ? alpha(SELECTED_GOLD, 0.22) : alpha(meta.color, 0.14),
            color: selected ? SELECTED_GREEN : meta.color,
            "& .MuiSvgIcon-root": { fontSize: 18 },
          }}
        >
          {meta.Icon ? <meta.Icon /> : null}
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: 13,
              lineHeight: 1.2,
              color: selected ? crmColors.logo.dark : crmColors.text.primary,
            }}
          >
            {meta.short}
          </Typography>
          <Typography
            sx={{
              fontSize: 11,
              color: selected ? SELECTED_GREEN : crmColors.text.secondary,
              fontWeight: 700,
            }}
          >
            {selected ? "Selected" : "Tap to pick"}
          </Typography>
        </Box>
      </Stack>
      <Typography
        sx={{
          fontWeight: 800,
          fontSize: "1.2rem",
          color: selected ? crmColors.logo.dark : meta.color,
          lineHeight: 1.1,
        }}
      >
        {pct}%
      </Typography>
      <Typography
        sx={{
          fontSize: 11.5,
          fontWeight: 700,
          color: selected ? SELECTED_GREEN : crmColors.text.secondary,
        }}
      >
        {working} working
      </Typography>
    </Box>
  );
};

const ModuleUsage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [selectedModules, setSelectedModules] = useState([]);
  const [range, setRange] = useState(
    PERIODS.some((p) => p.value === searchParams.get("range"))
      ? searchParams.get("range")
      : "last_30_days"
  );
  const [status, setStatus] = useState(
    STATUS_OPTIONS.some((o) => o.value === searchParams.get("status"))
      ? searchParams.get("status")
      : "all"
  );
  const [sort, setSort] = useState(
    SORT_OPTIONS.some((o) => o.value === searchParams.get("sort"))
      ? searchParams.get("sort")
      : "score_desc"
  );
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [loading, setLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [moduleStats, setModuleStats] = useState([]);
  const [statsSummary, setStatsSummary] = useState(null);
  const [listSummary, setListSummary] = useState(null);
  const [rangeLabel, setRangeLabel] = useState("Last 30 Days");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const statsByModule = useMemo(() => {
    const map = {};
    (moduleStats || []).forEach((s) => {
      map[s.module] = s;
    });
    return map;
  }, [moduleStats]);

  //Created By Cursor — API 1: module % stats only
  const loadStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const res = await apiService.getSopModuleUsageStats({ range });
      if (res.success) {
        setModuleStats(res.module_stats || []);
        setRangeLabel(res.range_label || range);
        setDateFrom(res.date_from || "");
        setDateTo(res.date_to || "");
        setStatsSummary(res.summary || null);
      }
    } catch {
      /* keep prior stats */
    } finally {
      setStatsLoading(false);
    }
  }, [range]);

  //Created By Cursor — API 2: school list when modules selected
  const loadSchools = useCallback(async () => {
    if (selectedModules.length === 0) {
      setLoading(false);
      setRows([]);
      setTotal(0);
      setListSummary(null);
      return;
    }
    setLoading(true);
    try {
      const res = await apiService.getSopModuleUsage({
        q: query,
        range,
        modules: selectedModules,
        status,
        sort,
        limit: pageSize,
        offset: (page - 1) * pageSize,
      });
      if (res.success) {
        setRows(Array.isArray(res.data) ? res.data : []);
        setTotal(res.total || 0);
        setListSummary(res.summary || null);
        const maxPage = Math.max(1, Math.ceil((res.total || 0) / pageSize));
        if (page > maxPage) setPage(maxPage);
      } else {
        toast.error(res.error || res.message || "Failed to load schools");
        setRows([]);
        setTotal(0);
        setListSummary(null);
      }
    } catch (e) {
      toast.error(e?.message || "Failed to load schools");
      setRows([]);
      setTotal(0);
      setListSummary(null);
    } finally {
      setLoading(false);
    }
  }, [query, range, selectedModules, status, sort, page, pageSize]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  useEffect(() => {
    loadSchools();
  }, [loadSchools]);

  useEffect(() => {
    const t = setTimeout(() => {
      setPage(1);
      setQuery(search.trim());
    }, 350);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    const next = new URLSearchParams();
    next.set("range", range);
    next.set("status", status);
    next.set("sort", sort);
    if (selectedModules.length) {
      next.set("modules", selectedModules.join(","));
    }
    setSearchParams(next, { replace: true });
  }, [selectedModules, range, status, sort, setSearchParams]);

  //Created By Cursor — period change clears module selection (no school fetch)
  const onPeriodChange = (value) => {
    setPage(1);
    setSelectedModules([]);
    setRows([]);
    setTotal(0);
    setListSummary(null);
    setRange(value);
  };

  const toggleModule = (value) => {
    setPage(1);
    setSelectedModules((prev) => {
      if (prev.includes(value)) {
        return prev.filter((m) => m !== value);
      }
      if (prev.length >= 8) {
        toast.error("Pick up to 8 modules");
        return prev;
      }
      return [...prev, value];
    });
  };

  const pageCount = Math.max(1, Math.ceil(total / pageSize) || 1);
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);
  const totalActive =
    listSummary?.total_schools_active ?? statsSummary?.total_schools_active ?? 0;
  const allWorking = listSummary?.all_working ?? 0;
  const partial = listSummary?.partial ?? 0;
  const notWorking = listSummary?.not_working ?? 0;
  const dateLine =
    dateFrom && dateTo
      ? dateFrom === dateTo
        ? formatDate(dateFrom)
        : `${formatDate(dateFrom)} – ${formatDate(dateTo)}`
      : "";

  return (
    <PageContentLayout
      title="Module Usage"
      subtitle={`Top modules · ${rangeLabel}${dateLine ? ` · ${dateLine}` : ""}`}
      barSubtitle={
        selectedModules.length === 0
          ? "Select modules to load schools"
          : `${selectedModules.length} modules · ${allWorking} all working · ${partial} partial · ${notWorking} not`
      }
      breadcrumbs={[
        {
          label: "Module Usage",
          icon: <ModuleIcon sx={{ color: "#ffffff", fontSize: 18 }} />,
        },
      ]}
      backTo="/sop"
      darkBar
      actions={
        <Button
          variant="contained"
          size="small"
          onClick={() => navigate("/sop")}
          sx={{
            textTransform: "none",
            fontWeight: 800,
            borderRadius: "10px",
            bgcolor: crmColors.energy.main,
            "&:hover": { bgcolor: crmColors.energy.dark },
          }}
        >
          Back to SOP
        </Button>
      }
    >
      <Stack spacing={2}>
        <Box
          sx={{
            p: { xs: 1.35, md: 1.6 },
            borderRadius: "16px",
            border: `1px solid ${crmColors.border.default}`,
            background: `linear-gradient(165deg, ${alpha(crmColors.primary.main, 0.05)} 0%, ${
              crmColors.background.paper
            } 55%)`,
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            alignItems={{ sm: "center" }}
            justifyContent="space-between"
            sx={{ mb: 1.15 }}
          >
            <Box>
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 800,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  color: crmColors.text.secondary,
                }}
              >
                Period
              </Typography>
              <Typography sx={{ mt: 0.2, fontWeight: 800, fontSize: 14.5, color: crmColors.text.primary }}>
                {rangeLabel}
                {dateLine ? (
                  <Typography
                    component="span"
                    sx={{
                      ml: 0.75,
                      fontWeight: 600,
                      fontSize: 12.5,
                      color: crmColors.text.secondary,
                    }}
                  >
                    {dateLine}
                  </Typography>
                ) : null}
              </Typography>
            </Box>
          </Stack>
          <Stack
            direction="row"
            spacing={0.85}
            sx={{ mb: 1.75, width: 1 }}
          >
            {PERIODS.map((p) => {
              const selected = range === p.value;
              return (
                <Box
                  key={p.value}
                  onClick={() => onPeriodChange(p.value)}
                  sx={{
                    flex: 1,
                    px: 0.65,
                    py: 1,
                    minWidth: 0,
                    borderRadius: "10px",
                    cursor: "pointer",
                    textAlign: "center",
                    fontWeight: 800,
                    fontSize: { xs: 11, sm: 12 },
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    border: `1px solid ${
                      selected
                        ? alpha(crmColors.logo.brand, 0.55)
                        : alpha(crmColors.border.default, 0.95)
                    }`,
                    bgcolor: selected
                      ? alpha(crmColors.logo.brand, 0.16)
                      : alpha("#fff", 0.88),
                    color: selected ? crmColors.logo.dark : crmColors.text.primary,
                    boxShadow: selected
                      ? `0 4px 12px ${alpha(crmColors.logo.brand, 0.16)}`
                      : "none",
                    transition: "all 0.15s ease",
                    "&:hover": {
                      borderColor: alpha(crmColors.logo.brand, 0.4),
                      bgcolor: alpha(crmColors.logo.brand, selected ? 0.18 : 0.06),
                    },
                  }}
                >
                  {p.label}
                </Box>
              );
            })}
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.15 }}>
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 800,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                color: crmColors.text.secondary,
              }}
            >
              Pick top modules
            </Typography>
            {statsLoading ? (
              <CircularProgress size={12} sx={{ color: crmColors.logo.brand }} />
            ) : null}
          </Stack>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "repeat(2, minmax(0, 1fr))",
                sm: "repeat(3, minmax(0, 1fr))",
                md: "repeat(6, minmax(0, 1fr))",
              },
              gap: 1.15,
            }}
          >
            {MODULES.map((m) => (
              <ModulePickCard
                key={m.value}
                meta={m}
                selected={selectedModules.includes(m.value)}
                stats={statsByModule[m.value]}
                onToggle={() => toggleModule(m.value)}
              />
            ))}
          </Box>
        </Box>

        <Stack direction="row" spacing={1.15} useFlexGap flexWrap="wrap">
          <SummaryTile
            label="Active schools"
            value={totalActive}
            color={crmColors.primary.main}
            icon={<ModuleIcon />}
          />
          <SummaryTile
            label="All working"
            value={allWorking}
            color={crmColors.success.main}
            icon={<WorkingIcon />}
            active={status === "working"}
            onClick={() => {
              setPage(1);
              setStatus(status === "working" ? "all" : "working");
            }}
          />
          <SummaryTile
            label="Partial"
            value={partial}
            color={crmColors.warning.main}
            icon={<PartialIcon />}
            active={status === "partial"}
            onClick={() => {
              setPage(1);
              setStatus(status === "partial" ? "all" : "partial");
            }}
          />
          <SummaryTile
            label="Not working"
            value={notWorking}
            color={crmColors.error.main}
            icon={<NotWorkingIcon />}
            active={status === "not_working"}
            onClick={() => {
              setPage(1);
              setStatus(status === "not_working" ? "all" : "not_working");
            }}
          />
        </Stack>

        <Box
          sx={{
            p: 1.5,
            borderRadius: "14px",
            border: `1px solid ${crmColors.border.default}`,
            bgcolor: "#fff",
          }}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1.25}
            alignItems={{ md: "center" }}
          >
            <TextField
              size="small"
              placeholder="Search school, code, phone, working status…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18, color: "text.secondary" }} />
                  </InputAdornment>
                ),
              }}
              sx={{ ...fieldSx, minWidth: { md: 280 }, flex: 1 }}
            />
            <TextField
              select
              size="small"
              label="Status"
              value={status}
              onChange={(e) => {
                setPage(1);
                setStatus(e.target.value);
              }}
              sx={{ ...fieldSx, minWidth: 170 }}
            >
              {STATUS_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              label="Sort"
              value={sort}
              onChange={(e) => {
                setPage(1);
                setSort(e.target.value);
              }}
              sx={{ ...fieldSx, minWidth: 160 }}
            >
              {SORT_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              label="Show"
              value={pageSize}
              onChange={(e) => {
                setPage(1);
                setPageSize(Number(e.target.value));
              }}
              sx={{ ...fieldSx, minWidth: 120 }}
            >
              {PAGE_SIZES.map((n) => (
                <MenuItem key={n} value={n}>
                  {n} schools
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </Box>

        {selectedModules.length === 0 ? (
          <Box
            sx={{
              textAlign: "center",
              py: 7,
              px: 2,
              borderRadius: "16px",
              border: `1px dashed ${crmColors.border.default}`,
              bgcolor: alpha(crmColors.primary.main, 0.02),
            }}
          >
            <Typography sx={{ fontWeight: 700 }}>Pick modules above</Typography>
            <Typography variant="caption" color="text.secondary">
              Schools load only after you select one or more modules
            </Typography>
          </Box>
        ) : loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 7 }}>
            <CircularProgress size={28} sx={{ color: crmColors.primary.main }} />
          </Box>
        ) : rows.length === 0 ? (
          <Box
            sx={{
              textAlign: "center",
              py: 7,
              px: 2,
              borderRadius: "16px",
              border: `1px dashed ${crmColors.border.default}`,
              bgcolor: alpha(crmColors.primary.main, 0.02),
            }}
          >
            <Typography sx={{ fontWeight: 700 }}>No schools match</Typography>
            <Typography variant="caption" color="text.secondary">
              Try another period, status filter, or module set
            </Typography>
          </Box>
        ) : (
          <>
            <Stack spacing={1.1}>
              {rows.map((row, index) => {
                const tone = statusTone(row.overall_status);
                const serial = (page - 1) * pageSize + index + 1;
                return (
                  <Box
                    key={row.school_code}
                    sx={{
                      display: "flex",
                      borderRadius: "14px",
                      overflow: "hidden",
                      border: `1px solid ${crmColors.border.default}`,
                      background: `linear-gradient(120deg, ${crmColors.background.paper} 0%, ${alpha(
                        tone,
                        0.05
                      )} 100%)`,
                    }}
                  >
                    <Box
                      sx={{
                        width: 5,
                        alignSelf: "stretch",
                        bgcolor: tone,
                        flexShrink: 0,
                      }}
                    />
                    <Box
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        display: "flex",
                        flexWrap: "wrap",
                        alignItems: "center",
                        gap: 1.35,
                        px: 1.75,
                        py: 1.3,
                      }}
                    >
                      <Typography
                        sx={{
                          width: 36,
                          flexShrink: 0,
                          fontWeight: 800,
                          fontSize: 13,
                          color: crmColors.text.secondary,
                          textAlign: "center",
                        }}
                      >
                        {serial}
                      </Typography>
                      <Box sx={{ flex: "1 1 170px", minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 800, fontSize: 14.5 }}>
                          {row.school_name || "—"}
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: crmColors.text.secondary }}>
                          {row.school_code}
                          {row.admin_phone ? ` · ${row.admin_phone}` : ""}
                        </Typography>
                        {row.working_status ? (
                          <Chip
                            size="small"
                            label={row.working_status}
                            sx={{
                              mt: 0.5,
                              height: 22,
                              fontWeight: 700,
                              fontSize: 11,
                              bgcolor: alpha(crmColors.primary.main, 0.08),
                              color: crmColors.primary.main,
                            }}
                          />
                        ) : null}
                      </Box>

                      <Stack direction="row" spacing={0.7} flexWrap="wrap" useFlexGap>
                        {selectedModules.map((mod) => {
                          const meta = moduleMeta(mod);
                          const cell =
                            row.modules && typeof row.modules === "object"
                              ? row.modules[mod] || { working: 0, count: 0 }
                              : { working: 0, count: 0 };
                          const ok = Number(cell.working) === 1;
                          const count = Number(cell.count) || 0;
                          const ModIcon = meta.Icon;
                          return (
                            <Chip
                              key={mod}
                              size="small"
                              icon={
                                ModIcon ? (
                                  <ModIcon sx={{ fontSize: "14px !important" }} />
                                ) : undefined
                              }
                              label={`${meta.short}${ok && count ? ` · ${count}` : ""}`}
                              sx={{
                                height: 26,
                                fontWeight: 800,
                                bgcolor: ok
                                  ? alpha(crmColors.success.main, 0.12)
                                  : alpha(crmColors.error.main, 0.1),
                                color: ok
                                  ? crmColors.success.dark || crmColors.success.main
                                  : crmColors.error.main,
                                border: `1px solid ${alpha(
                                  ok ? crmColors.success.main : crmColors.error.main,
                                  0.22
                                )}`,
                              }}
                            />
                          );
                        })}
                      </Stack>

                      <Chip
                        size="small"
                        label={`${row.working_modules || 0}/${row.modules_total || selectedModules.length} · ${
                          row.overall_status || "—"
                        }`}
                        sx={{
                          height: 26,
                          fontWeight: 800,
                          bgcolor: alpha(tone, 0.12),
                          color: tone,
                        }}
                      />

                      <Button
                        size="small"
                        variant="contained"
                        disableElevation
                        disabled={!row.school_code}
                        startIcon={<DetailsIcon sx={{ fontSize: 14 }} />}
                        onClick={() =>
                          navigate(`/sop/school/${encodeURIComponent(row.school_code)}`)
                        }
                        sx={{
                          textTransform: "none",
                          fontWeight: 800,
                          fontSize: 11.5,
                          height: 30,
                          minWidth: 88,
                          px: 1.25,
                          borderRadius: "8px",
                          ml: { xs: 0, md: "auto" },
                          bgcolor: crmColors.primary.main,
                          "&:hover": { bgcolor: crmColors.primary.dark },
                        }}
                      >
                        Explore
                      </Button>
                    </Box>
                  </Box>
                );
              })}
            </Stack>

            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.25}
              alignItems="center"
              justifyContent="space-between"
              sx={{ mt: 0.5, px: 0.5 }}
            >
              <Typography sx={{ fontSize: 12.5, color: crmColors.text.secondary, fontWeight: 600 }}>
                Showing {from}–{to} of {total}
              </Typography>
              <Pagination
                color="primary"
                shape="rounded"
                size="small"
                count={pageCount}
                page={page}
                onChange={(_, p) => setPage(p)}
                siblingCount={1}
                boundaryCount={1}
                disabled={loading || pageCount <= 1}
                sx={{ "& .MuiPaginationItem-root": { fontWeight: 700 } }}
              />
            </Stack>
          </>
        )}
      </Stack>
    </PageContentLayout>
  );
};

export default ModuleUsage;
